<script setup lang="ts">
const route = useRoute()
const toast = useToast()
const siteUrl = useRuntimeConfig().public.siteUrl

useHead({
  meta: [
    { charset: 'utf-8' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { key: 'theme-color', name: 'theme-color', content: '#020618' }
  ],
  link: [
    { rel: 'icon', href: '/favicon.ico' },
    // A 48px-multiple PNG so Google can show a sharp favicon next to results
    { rel: 'icon', type: 'image/png', sizes: '192x192', href: '/android-chrome-192x192.png' },
    { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
    { rel: 'manifest', href: '/site.webmanifest' },
    // Site-wide canonical: attributes every page (including prerendered ones
    // reachable on custom domains) to mychar.gg. Character pages override
    // this with the custom domain when one is active (same key).
    { key: 'canonical', rel: 'canonical', href: () => `${siteUrl}${route.path === '/' ? '' : route.path}` }
  ],
  htmlAttrs: {
    lang: 'en'
  }
})

useSeoMeta({
  titleTemplate: '%s - mychar.gg',
  ogImage: 'https://mychar.gg/og-image.png',
  // og:url + og:type complete the core OG set on every page (crawlers flag
  // pages missing them). Character pages override ogUrl with their canonical
  // (custom-domain aware); blog posts override ogType with 'article'.
  ogUrl: () => `${siteUrl}${route.path === '/' ? '' : route.path}`,
  ogType: 'website',
  twitterImage: 'https://mychar.gg/og-image.png',
  twitterCard: 'summary_large_image'
})

// Sitewide JSON-LD identity. Every page's graph links back to these via @id;
// page-specific nodes (ProfilePage/Person, Product, Article) attach per route.
// The brand is always "mychar.gg": the bare word "mychar" collides with MyChart
// and other products in search, so "mychar" is only declared as an alternate name.
// sameAs lists every official profile (all named mychar.gg / @mychargg); add new
// ones here as they exist (TikTok, YouTube, GitHub, LinkedIn, Product Hunt).
useSchemaOrg([
  defineOrganization({
    name: 'mychar.gg',
    alternateName: 'mychar',
    // Root-relative on purpose: the module resolves it against site.url into
    // the #logo ImageObject. (It also emits a second minimal #organization
    // node next to #identity - that is intended library behavior, not a bug.)
    logo: '/logo_short_transparent.png',
    description: 'mychar.gg is a highly customizable link-in-bio platform: one page for your whole personal brand, designed in a drag-and-drop editor that shows every change live. Anyone can use it, and it adds integrations most link-in-bio tools lack for creators, streamers, and gamers, such as YouTube, Twitch, Discord, video clips, and a music player.',
    slogan: 'The link in bio you design yourself.',
    email: 'support@mychar.gg',
    address: { '@type': 'PostalAddress', 'addressLocality': 'Breda', 'addressCountry': 'NL' },
    sameAs: [
      'https://x.com/mychargg',
      'https://www.instagram.com/mychargg/',
      'https://www.tiktok.com/@mychar.gg',
      'https://www.youtube.com/@mychargg',
      'https://discord.gg/mychar'
    ]
  }),
  // name + alternateName feed the site name Google shows in results
  defineWebSite({
    name: 'mychar.gg',
    alternateName: ['mychar', 'mychar gg'],
    description: 'The highly customizable link in bio. One page for your whole personal brand.'
  })
])

// Only consumed by the ClientOnly search dialog below - fetched client-side
// (browser WASM sqlite), never during SSR where serverless has no content
// database. The docs sidebar has its own build-time fetch in layouts/docs.vue.
const { data: navigation } = useLazyAsyncData('search-navigation', () => queryCollectionNavigation('docs').catch(() => []), {
  server: false,
  transform: data => data?.find(item => item.path === '/docs')?.children || [],
  default: () => []
})
const { data: files } = useLazyAsyncData('search', () => queryCollectionSearchSections('docs').catch(() => []), {
  server: false,
  default: () => []
})

const links = [{
  label: 'Docs',
  icon: 'i-lucide-book',
  to: '/docs/getting-started'
}, {
  label: 'Pricing',
  icon: 'i-lucide-credit-card',
  to: '/pricing'
}]

// Handle auth errors from URL params
onMounted(async () => {
  await nextTick()

  try {
    const error = route.query.error as string
    const errorCode = route.query.error_code as string
    const errorDescription = route.query.error_description as string

    // /confirm handles its own link errors (and redirects), skip to avoid a second toast
    if (error && route.path !== '/confirm') {
      let title = 'Authentication Error'
      let description = String(errorDescription || error)
      // Expired links get a button to the matching "send a new link" page
      let newLinkTo: string | null = null

      // Map common error codes to user-friendly messages
      if (errorCode === 'otp_expired') {
        title = 'Link Expired'
        if (route.path.startsWith('/reset-password')) {
          description = 'This password reset link has expired.'
          newLinkTo = '/request-password'
        } else {
          description = 'This email link has expired.'
          newLinkTo = '/email-sent?reason=expired'
        }
      } else if (errorCode === 'access_denied') {
        title = 'Access Denied'
        description = 'The authentication link is invalid or has expired.'
      }

      toast.add({
        title: String(title),
        description: String(description),
        color: 'error',
        icon: 'i-lucide-alert-circle',
        ...(newLinkTo
          ? {
              duration: 0,
              actions: [{ label: 'Request a new link', color: 'neutral' as const, variant: 'outline' as const, onClick: () => { navigateTo(newLinkTo) } }]
            }
          : {})
      })

      // Clean up URL by removing error params
      const cleanQuery = { ...route.query }
      delete cleanQuery.error
      delete cleanQuery.error_code
      delete cleanQuery.error_description

      await navigateTo({
        path: route.path,
        query: Object.keys(cleanQuery).length > 0 ? cleanQuery : undefined,
        hash: ''
      }, { replace: true })
    }
  } catch (err) {
    console.error('Error handling auth error params:', err)
  }
})
</script>

<template>
  <UApp>
    <NuxtLoadingIndicator />

    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <ClientOnly>
      <LazyUContentSearch
        :files="files"
        shortcut="meta_k"
        :navigation="navigation"
        :links="links"
        :fuse="{ resultLimit: 42 }"
      />
    </ClientOnly>
  </UApp>
</template>
